import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  SvgIcon,
} from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import {
  AccountBalanceWallet,
  AccessTime,
  Apartment,
  ArrowDropDown,
  Article,
  CalendarMonth,
  CreditCard,
  ExpandMore,
  FilterList,
  DonutLarge,
  Groups,
  KeyboardArrowDown,
  LocalFlorist,
  LocalShipping,
  People,
  PersonOutline,
  Refresh,
  Savings,
  Search,
  ShoppingCart,
  SupportAgent,
  TrendingDown,
  TrendingUp,
  WarningAmber,
  Payments,
  PendingActions,
  AssignmentLate,
  Assignment,
  FactCheck,
} from "@mui/icons-material";
import Chart from "react-apexcharts";
import { usePageTitle } from "contexts/PageTitleContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  backgroundImage: "none",
  height: "100%",
}));

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "₹ 0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  })
    .format(Number(value))
    .replace("₹", "₹ ");
};

const formatNumber = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "0";
  return new Intl.NumberFormat("en-IN").format(Number(value));
};

const formatDateTime = (date) =>
  date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  overflow: "visible",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 14,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.75, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  borderRadius: "14px 14px 0 0",
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
}));

const DatePickerField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
  },
  "& .MuiInputBase-input": {
    cursor: "pointer",
    colorScheme: theme.palette.mode === "dark" ? "dark" : "light",
  },
}));

const KPI_META = [
  { key: "total_revenue", label: "Total Revenue", icon: Payments, color: "#16A34A", currency: true },
  { key: "full_payment", label: "Full Payment", icon: AccountBalanceWallet, color: "#2563EB", currency: true },
  { key: "advance_payment", label: "Advance Payment", icon: Savings, color: "#7C3AED", currency: true },
  { key: "outstanding_amount", label: "Outstanding Amount", icon: WarningAmber, color: "#EA580C", currency: true, negativeTrend: true, fullLabel: true },
  { key: "credit", label: "Credit", icon: CreditCard, color: "#DC2626", currency: true, negativeTrend: true },
  { key: "total_farmers", label: "Total Farmers", icon: People, color: "#0D9488", currency: false },
  { key: "total_orders", label: "Total Orders", icon: ShoppingCart, color: "#2563EB", currency: false },
  { key: "total_posts", label: "Total Posts", icon: Article, color: "#7C3AED", currency: false },
];

const STATUS_COLORS = ["#F59E0B", "#22C55E", "#3B82F6", "#A855F7", "#10B981", "#EF4444", "#64748B"];

const getMetricChange = (item) => {
  if (!item || typeof item !== "object") return null;
  const raw =
    item.change ??
    item.percentage_change ??
    item.percent_change ??
    item.growth ??
    item.growth_percentage ??
    item.vs_last_period;
  if (raw == null || raw === "") return null;
  const num = Number(raw);
  if (Number.isNaN(num)) return null;
  return num;
};

const findActivity = (activities, label) =>
  (activities || []).find(
    (a) => String(a?.label || "").trim().toLowerCase() === String(label).trim().toLowerCase()
  );

const findActivityValue = (activities, label) => {
  const row = findActivity(activities, label);
  return Number(row?.value ?? row?.count ?? 0);
};

const pickNamedArray = (source, key) => {
  const walk = (node, depth) => {
    if (!node || typeof node !== "object" || Array.isArray(node) || depth > 5) return null;
    if (Array.isArray(node[key])) return node[key];
    for (const value of Object.values(node)) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const found = walk(value, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(source, 0) || [];
};

const DISTRICT_FIELDS = ["Pune", "Amravati", "Buldana", "Jalgaon", "Jalna", "Other Districts"];

const canonicalDistrict = (name = "") => {
  const key = String(name).trim().toLowerCase().replace(/\s+/g, " ");
  if (key === "pune") return "Pune";
  if (key === "amravati" || key === "amravti") return "Amravati";
  if (key === "buldana" || key === "buldhana") return "Buldana";
  if (key === "jalgaon") return "Jalgaon";
  if (key === "jalna") return "Jalna";
  if (key.includes("other")) return "Other Districts";
  return "";
};

const CROP_FIELDS = [
  "Banana",
  "Bamboo",
  "Date Palm",
  "Avocado",
  "Dragon Fruit",
  "Coconut",
  "Guava",
  "Pomegranate",
];

const getCropEmoji = (label = "") => {
  const name = String(label).toLowerCase();
  if (name.includes("banana")) return "🍌";
  if (name.includes("bamboo")) return "🎋";
  if (name.includes("date")) return "🌴";
  if (name.includes("avocado")) return "🥑";
  if (name.includes("dragon")) return "🍓";
  if (name.includes("coconut")) return "🥥";
  if (name.includes("guava")) return "🍐";
  if (name.includes("pomegranate")) return "🍎";
  if (name.includes("grape")) return "🍇";
  if (name.includes("other")) return "🍇";
  if (name.includes("mango")) return "🥭";
  if (name.includes("apple")) return "🍎";
  if (name.includes("wheat") || name.includes("rice") || name.includes("crop")) return "🌾";
  return "🌱";
};

const KpiCard = ({ label, value, change, icon: Icon, color, negativeTrend, fullLabel = false }) => {
  const hasChange = change != null && !Number.isNaN(Number(change));
  const changeNum = hasChange ? Number(change) : 0;
  const isUp = changeNum >= 0;
  const TrendIcon = isUp ? TrendingUp : TrendingDown;
  const trendColor = negativeTrend
    ? "#DC2626"
    : isUp
      ? "#16A34A"
      : "#DC2626";

  return (
    <SectionCard
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        height: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 1.25 }}
          alignItems={fullLabel ? "flex-start" : "center"}
          sx={{ minWidth: 0 }}
        >
          <Box
            sx={{
              width: { xs: 32, sm: 36 },
              height: { xs: 32, sm: 36 },
              borderRadius: 1.5,
              bgcolor: alpha(color, 0.12),
              color,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            noWrap={!fullLabel}
            title={label}
            sx={{
              minWidth: 0,
              flex: 1,
              ...(fullLabel
                ? {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.25,
                    pt: 0.25,
                  }
                : {}),
            }}
          >
            {label}
          </Typography>
        </Stack>
        <Typography
          title={String(value)}
          sx={{
            fontWeight: 700,
            fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.95rem" },
            lineHeight: 1.25,
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            textAlign: "center",
          }}
        >
          {value}
        </Typography>
        {hasChange ? (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.35} sx={{ color: trendColor, minWidth: 0 }}>
            <TrendIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            <Typography variant="caption" fontWeight={600} noWrap>
              {`${isUp ? "+" : ""}${changeNum.toFixed(1)}%`}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </SectionCard>
  );
};

const RankListItem = ({ rank, title, subtitle, value, percent, color }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{
      py: 1,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Avatar
      sx={{
        width: 36,
        height: 36,
        bgcolor: alpha(color, 0.12),
        color,
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {rank}
    </Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap title={title}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" noWrap>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
    <Box sx={{ textAlign: "right" }}>
      <Typography variant="body2" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {percent}
      </Typography>
    </Box>
  </Stack>
);

const NewDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const isCompanyUser = apiService.isCompanyUser?.();
  const companyId = apiService.getCompanyId?.();
  const companyDetails = apiService.getCompanyDetails?.();

  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [username, setUsername] = useState("CMD User");
  const [showFilters, setShowFilters] = useState(true);
  const [orderType, setOrderType] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [companiesMenuOpen, setCompaniesMenuOpen] = useState(false);
  const [pendingCompanyIds, setPendingCompanyIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(
    isCompanyUser && companyId ? [String(companyId)] : []
  );
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [salesAnalyticsType, setSalesAnalyticsType] = useState("daily");
  const [farmersAnalyticsType, setFarmersAnalyticsType] = useState("monthly");
  const [ordersAnalyticsType, setOrdersAnalyticsType] = useState("monthly");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [useDateFilter, setUseDateFilter] = useState(true);
  const [periodFilter, setPeriodFilter] = useState("this_month");

  const getDefaultFilters = () => {
    const today = new Date().toISOString().split("T")[0];
    return {
      fromDate: today,
      toDate: today,
      company: isCompanyUser && companyId ? [String(companyId)] : [],
      category: "all",
      orderType: "all",
    };
  };
  const [appliedFilters, setAppliedFilters] = useState(() => getDefaultFilters());

  const [loading, setLoading] = useState(true);
  const [salesOverview, setSalesOverview] = useState({});
  const [ordersByStatusList, setOrdersByStatusList] = useState([]);
  const [activityMetrics, setActivityMetrics] = useState({
    total_revenue: 0,
    full_payment: 0,
    outstanding_amount: 0,
    advance_payment: 0,
    credit: 0,
    pending_enquiries: 0,
    talk_to_agronomist: { total: 0, connected: 0, pending: 0 },
    total_activity: 0,
    activities: [],
  });
  const [byProduct, setByProduct] = useState({ category_performance: [], category_growth: [] });
  const [topProducts, setTopProducts] = useState([]);
  const [companyWiseRows, setCompanyWiseRows] = useState([]);
  const [farmersAnalyticsData, setFarmersAnalyticsData] = useState([]);
  const [farmersStateWise, setFarmersStateWise] = useState([]);
  const [farmersDistrictWise, setFarmersDistrictWise] = useState([]);
  const [companyWiseCropSales, setCompanyWiseCropSales] = useState([]);
  const [cropCompanyFilter, setCropCompanyFilter] = useState("all");
  const [cropCompanyOptions, setCropCompanyOptions] = useState([]);
  const [farmersAnalyticsLoading, setFarmersAnalyticsLoading] = useState(true);
  const [farmersAnalyticsError, setFarmersAnalyticsError] = useState("");
  const [error, setError] = useState("");

  const salesAnalyticsTypeOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];
  const ordersAnalyticsTypeOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "yearly", label: "Yearly" },
    { value: "monthly", label: "This Month" },
  ];
  const farmersAnalyticsTypeOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "This Month" },
    { value: "yearly", label: "Yearly" },
  ];

  useEffect(() => {
    setPageTitle("CMD Dashboard");
    try {
      const logged = JSON.parse(localStorage.getItem("logged_data") || "{}");
      const name =
        logged?.data?.username ||
        logged?.data?.name ||
        logged?.data?.full_name ||
        localStorage.getItem("username") ||
        "CMD User";
      setUsername(name);
    } catch {
      setUsername("CMD User");
    }
  }, [setPageTitle]);

  // Build query string with filters for analytics APIs (same as Old Dashboard)
  const buildQueryString = (filters = appliedFilters, options = {}) => {
    const { includeAnalyticsType = false, analyticsType } = options;
    const params = new URLSearchParams();
    const {
      fromDate,
      toDate,
      company,
      category,
      orderType: selectedOrderType,
    } = filters;

    let companyParam = "";
    if (isCompanyUser) {
      companyParam = String(companyId || "");
    } else if (Array.isArray(company)) {
      companyParam = company.length > 0 ? company.join(",") : "";
    } else if (company && company !== "all") {
      companyParam = company;
    }
    if (companyParam) {
      params.set("company", companyParam);
    }
    if (category && category !== "all") {
      params.set("category", category);
    } else {
      params.set("category", "");
    }

    const apiOrderType = selectedOrderType === "normal" ? "app" : selectedOrderType;
    params.set("order_type", apiOrderType);

    if (includeAnalyticsType && analyticsType) {
      params.set("analytics_type", analyticsType);
    }

    if (fromDate) {
      params.set("from_date", fromDate);
    }
    if (toDate) {
      params.set("to_date", toDate);
    }

    return `?${params.toString()}`;
  };

  const fetchCompanies = async () => {
    try {
      if (isCompanyUser && companyDetails) {
        setCompanies([companyDetails]);
        return;
      }
      const response = await apiService.get("e-commerce/company/get-company-for-dashboard/");
      if (response?.success) {
        const rawCompanies = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.Company)
            ? response.data.Company
            : [];
        const mappedCompanies = rawCompanies
          .map((company, index) => ({
            id: company?.id ?? company?.company_id ?? company?.value ?? index,
            name:
              company?.name ??
              company?.company_name ??
              company?.label ??
              String(company?.value ?? ""),
          }))
          .filter((company) => company.name);
        setCompanies(mappedCompanies);
      } else {
        setCompanies([]);
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
      setCompanies([]);
    }
  };

  const getCategoryCompanyValue = (companySelection) => {
    if (isCompanyUser && companyId) return String(companyId);
    if (Array.isArray(companySelection)) {
      return companySelection.length === 1 ? companySelection[0] : "all";
    }
    return companySelection || "all";
  };

  const fetchCategories = async (companyValue = "all") => {
    try {
      let response;
      if (companyValue === "all") {
        response = await apiService.get(API_ENDPOINTS.GET_product_categories);
      } else {
        const userId = localStorage.getItem("user_id");
        response = await apiService.get(
          `${API_ENDPOINTS.GET_product_category}?company=${companyValue}&user_id=${userId}`
        );
      }
      if (response?.success) {
        setCategories(response?.data?.ProductCategory || []);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  const pickActivityMetricsFromResponse = (activityRes) => {
    const metricsCandidates = [
      activityRes?.data?.data,
      activityRes?.data,
      activityRes,
    ].filter(Boolean);

    const pickMetricsPayload =
      metricsCandidates.find((payload) => {
        if (Array.isArray(payload?.activities) && payload.activities.length > 0) return true;
        return payload?.total_revenue != null || payload?.total_activity != null;
      }) || {};

    return {
      total_revenue: Number(pickMetricsPayload?.total_revenue || 0),
      full_payment: Number(pickMetricsPayload?.full_payment || 0),
      advance_payment: Number(pickMetricsPayload?.advance_payment || 0),
      outstanding_amount: Number(pickMetricsPayload?.outstanding_amount || 0),
      credit: Number(pickMetricsPayload?.credit || 0),
    };
  };

  const fetchCompanyWiseRevenue = async (
    filters = appliedFilters,
    companiesList = companies
  ) => {
    const selectedIds = Array.isArray(filters.company)
      ? filters.company.map(String)
      : filters.company && filters.company !== "all"
        ? [String(filters.company)]
        : [];
    const targetCompanies = selectedIds.length
      ? companiesList.filter((c) => selectedIds.includes(String(c.id)))
      : companiesList;

    if (!targetCompanies.length) {
      setCompanyWiseRows([]);
      return;
    }

    try {
      const rows = await Promise.all(
        targetCompanies.map(async (company) => {
          const qs = buildQueryString({
            ...filters,
            company: [String(company.id)],
          });
          const activityRes = await apiService.get(
            API_ENDPOINTS.GET_farmer_engagement_activity_metrics + qs
          );
          const metrics = pickActivityMetricsFromResponse(activityRes);
          return {
            company: company.name,
            total_revenue: metrics.total_revenue,
            full_payment: metrics.full_payment,
            advance_payment: metrics.advance_payment,
            pending_amount: metrics.outstanding_amount,
            credit_orders: metrics.credit,
          };
        })
      );
      setCompanyWiseRows(rows);
    } catch (err) {
      console.error("Error fetching company-wise revenue:", err);
      setCompanyWiseRows(
        targetCompanies.map((company) => ({
          company: company.name,
          total_revenue: 0,
          full_payment: 0,
          advance_payment: 0,
          pending_amount: 0,
          credit_orders: 0,
        }))
      );
    }
  };

  const fetchSalesOverview = async (
    filters = appliedFilters,
    analyticsType = salesAnalyticsType
  ) => {
    const qs = buildQueryString(filters, { includeAnalyticsType: true, analyticsType });
    const overviewRes = await apiService.get(API_ENDPOINTS.GET_sales_revenue_overview + qs);
    setSalesOverview(overviewRes?.data || {});
    setLastUpdated(new Date());
  };

  const fetchOrdersByStatus = async (
    filters = appliedFilters,
    analyticsType = ordersAnalyticsType
  ) => {
    const qs = buildQueryString(filters, { includeAnalyticsType: true, analyticsType });
    const overviewRes = await apiService.get(API_ENDPOINTS.GET_sales_revenue_overview + qs);
    const list = Array.isArray(overviewRes?.data?.orders_by_status)
      ? overviewRes.data.orders_by_status
      : [];
    setOrdersByStatusList(list);
  };

  const fetchFarmersAnalytics = async (
    filters = appliedFilters,
    analyticsType = farmersAnalyticsType
  ) => {
    setFarmersAnalyticsLoading(true);
    setFarmersAnalyticsError("");
    try {
      const qs = buildQueryString(filters, { includeAnalyticsType: true, analyticsType });
      const payload = await apiService.get(
        API_ENDPOINTS.GET_farmer_engagement_farmers_analytics + qs
      );
      const normalizedList = Array.isArray(payload?.data?.analytics)
        ? payload.data.analytics
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.analytics)
            ? payload.analytics
            : [];
      const stateWise = Array.isArray(payload?.data?.farmer_state_wise)
        ? payload.data.farmer_state_wise
        : Array.isArray(payload?.farmer_state_wise)
          ? payload.farmer_state_wise
          : Array.isArray(payload?.data?.data?.farmer_state_wise)
            ? payload.data.data.farmer_state_wise
            : [];
      const districtWise = pickNamedArray(payload, "farmer_district_wise");
      const companyCrops = pickNamedArray(payload, "company_wise_crop_sales");
      setFarmersAnalyticsData(normalizedList);
      setFarmersStateWise(stateWise);
      setFarmersDistrictWise(districtWise);
      setCompanyWiseCropSales(companyCrops);
      const companyNames = companyCrops
        .map((row) => String(row?.company || row?.company_name || "").trim())
        .filter(Boolean);
      if (companyNames.length) {
        setCropCompanyOptions((prev) =>
          Array.from(new Set([...(prev || []), ...companyNames]))
        );
      }
    } catch (err) {
      setFarmersAnalyticsError(
        typeof err === "string" ? err : err?.message || "Failed to load farmers analytics"
      );
      setFarmersAnalyticsData([]);
      setFarmersStateWise([]);
      setFarmersDistrictWise([]);
      setCompanyWiseCropSales([]);
    } finally {
      setFarmersAnalyticsLoading(false);
    }
  };

  const fetchFarmersCropByCompany = async (companyName = "all") => {
    try {
      let companyIds = appliedFilters.company;
      if (companyName !== "all") {
        const matched = (companies || []).find(
          (c) =>
            String(c?.name || "").trim().toLowerCase() ===
            String(companyName).trim().toLowerCase()
        );
        companyIds = matched?.id != null ? [String(matched.id)] : [companyName];
      }
      const filters = { ...appliedFilters, company: companyIds };
      const qs = buildQueryString(filters, {
        includeAnalyticsType: true,
        analyticsType: farmersAnalyticsType,
      });
      const payload = await apiService.get(
        API_ENDPOINTS.GET_farmer_engagement_farmers_analytics + qs
      );
      const companyCrops = pickNamedArray(payload, "company_wise_crop_sales");
      setCompanyWiseCropSales(companyCrops);
      const companyNames = companyCrops
        .map((row) => String(row?.company || row?.company_name || "").trim())
        .filter(Boolean);
      if (companyNames.length) {
        setCropCompanyOptions((prev) =>
          Array.from(new Set([...(prev || []), ...companyNames]))
        );
      }
    } catch (err) {
      console.error("Failed to load company crop sales:", err);
    }
  };

  const handleCropCompanyChange = (companyName) => {
    setCropCompanyFilter(companyName);
    fetchFarmersCropByCompany(companyName);
  };

  const fetchAllData = async (filters = appliedFilters) => {
    setLoading(true);
    setError("");
    try {
      const baseQs = buildQueryString(filters);
      const salesQs = buildQueryString(filters, {
        includeAnalyticsType: true,
        analyticsType: salesAnalyticsType,
      });
      const [activityRes, overviewRes, byProductRes, byCategoryRes] = await Promise.all([
        apiService.get(API_ENDPOINTS.GET_farmer_engagement_activity_metrics + baseQs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_overview + salesQs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_by_product + baseQs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_by_category + baseQs),
        fetchFarmersAnalytics(filters, farmersAnalyticsType),
        fetchOrdersByStatus(filters, ordersAnalyticsType),
      ]);
      setSalesOverview(overviewRes?.data || {});

      const metricsCandidates = [
        activityRes?.data?.data,
        activityRes?.data,
        activityRes,
      ].filter(Boolean);

      const pickMetricsPayload = metricsCandidates.find((payload) => {
        if (Array.isArray(payload?.activities) && payload.activities.length > 0) return true;
        return payload?.total_revenue != null || payload?.total_activity != null;
      }) || {};
      const rawActivities = pickMetricsPayload?.activities;
      const metricsActivities = Array.isArray(rawActivities)
        ? rawActivities
        : (rawActivities && typeof rawActivities === "object" ? Object.values(rawActivities) : []);
      const rawTotalRevenue = pickMetricsPayload?.total_revenue ?? 0;
      const derivedTotalActivity = pickMetricsPayload?.total_activity != null
        ? Number(pickMetricsPayload.total_activity || 0)
        : metricsActivities.reduce((sum, item) => sum + Number(item?.value || 0), 0);
      setActivityMetrics({
        total_revenue: Number(rawTotalRevenue || 0),
        full_payment: Number(pickMetricsPayload?.full_payment || 0),
        outstanding_amount: Number(pickMetricsPayload?.outstanding_amount || 0),
        advance_payment: Number(pickMetricsPayload?.advance_payment || 0),
        credit: Number(pickMetricsPayload?.credit || 0),
        pending_enquiries: Number(pickMetricsPayload?.pending_enquiries || 0),
        talk_to_agronomist: {
          total: Number(pickMetricsPayload?.talk_to_agronomist?.total || 0),
          connected: Number(pickMetricsPayload?.talk_to_agronomist?.connected || 0),
          pending: Number(pickMetricsPayload?.talk_to_agronomist?.pending || 0),
        },
        total_activity: derivedTotalActivity,
        activities: metricsActivities,
      });

      const activityStateWise = Array.isArray(activityRes?.data?.farmer_state_wise)
        ? activityRes.data.farmer_state_wise
        : Array.isArray(activityRes?.data?.data?.farmer_state_wise)
          ? activityRes.data.data.farmer_state_wise
          : Array.isArray(pickMetricsPayload?.farmer_state_wise)
            ? pickMetricsPayload.farmer_state_wise
            : [];
      if (activityStateWise.length) {
        setFarmersStateWise((prev) => (prev.length ? prev : activityStateWise));
      }

      const activityDistrictWise = pickNamedArray(activityRes, "farmer_district_wise");
      if (activityDistrictWise.length) {
        setFarmersDistrictWise((prev) => (prev.length ? prev : activityDistrictWise));
      }

      const activityCompanyCrops = pickNamedArray(activityRes, "company_wise_crop_sales");
      if (activityCompanyCrops.length) {
        setCompanyWiseCropSales((prev) => (prev.length ? prev : activityCompanyCrops));
        const companyNames = activityCompanyCrops
          .map((row) => String(row?.company || row?.company_name || "").trim())
          .filter(Boolean);
        if (companyNames.length) {
          setCropCompanyOptions((prev) =>
            Array.from(new Set([...(prev || []), ...companyNames]))
          );
        }
      }

      setByProduct({
        category_performance: Array.isArray(byCategoryRes?.data?.category_performance)
          ? byCategoryRes.data.category_performance
          : Array.isArray(byCategoryRes?.category_performance)
            ? byCategoryRes.category_performance
            : [],
        category_growth: Array.isArray(byCategoryRes?.data?.category_growth)
          ? byCategoryRes.data.category_growth
          : Array.isArray(byCategoryRes?.category_growth)
            ? byCategoryRes.category_growth
            : [],
      });
      setTopProducts(Array.isArray(byProductRes?.data) ? byProductRes.data : []);
      setLastUpdated(new Date());
      await fetchCompanyWiseRevenue(filters, companies);
    } catch (err) {
      console.error(err);
      setError(typeof err === "string" ? err : err?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    if (companies.length) {
      fetchCompanyWiseRevenue(appliedFilters, companies);
    }
  }, [companies]);

  useEffect(() => {
    const initializeFilters = async () => {
      await fetchCompanies();
      const initialCompany = isCompanyUser && companyId ? [String(companyId)] : selectedCompany;
      await fetchCategories(getCategoryCompanyValue(initialCompany));
    };
    initializeFilters();
  }, []);

  const handleApplyFilters = () => {
    const nextAppliedFilters = {
      fromDate: useDateFilter ? startDate : "",
      toDate: useDateFilter ? endDate : "",
      company: isCompanyUser && companyId ? [String(companyId)] : selectedCompany,
      category: selectedCategory,
      orderType,
    };
    setAppliedFilters(nextAppliedFilters);
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value;
    const companyIds = (typeof value === "string" ? value.split(",") : value).map(String);
    setPendingCompanyIds(companyIds);
  };

  const applyCompanySelection = async (companyIds) => {
    setSelectedCompany(companyIds);
    setSelectedCategory("all");
    await fetchCategories(getCategoryCompanyValue(companyIds));
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleOrderTypeChange = (event) => {
    setOrderType(event.target.value);
  };

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleResetFilters = () => {
    const defaults = getDefaultFilters();
    setStartDate(defaults.fromDate);
    setEndDate(defaults.toDate);
    setOrderType(defaults.orderType);
    setSalesAnalyticsType("daily");
    setSelectedCompany(defaults.company);
    setSelectedCategory(defaults.category);
    setUseDateFilter(true);
    setAppliedFilters(defaults);
    setTimeout(() => fetchCategories(getCategoryCompanyValue(defaults.company)), 0);
  };

  const activities = useMemo(
    () => (Array.isArray(activityMetrics?.activities) ? activityMetrics.activities : []),
    [activityMetrics]
  );

  const metrics = useMemo(
    () => ({
      total_revenue: Number(activityMetrics?.total_revenue || 0),
      full_payment: Number(activityMetrics?.full_payment || 0),
      advance_payment: Number(activityMetrics?.advance_payment || 0),
      outstanding_amount: Number(activityMetrics?.outstanding_amount || 0),
      credit: Number(activityMetrics?.credit || 0),
      total_farmers: findActivityValue(activities, "Total Farmers"),
      total_orders: findActivityValue(activities, "Total Orders"),
      total_posts: findActivityValue(activities, "Total Posts"),
      total_enquiries: findActivityValue(activities, "Total Enquiries"),
      total_field_visits: findActivityValue(activities, "Total Field Visits"),
    }),
    [activityMetrics, activities]
  );

  const metricChanges = useMemo(
    () => ({
      total_revenue: getMetricChange(activityMetrics),
      full_payment: getMetricChange(findActivity(activities, "Full Payment") || {}),
      advance_payment: getMetricChange(findActivity(activities, "Advance Payment") || {}),
      outstanding_amount: getMetricChange(findActivity(activities, "Outstanding Amount") || {}),
      credit: getMetricChange(findActivity(activities, "Credit") || {}),
      total_farmers: getMetricChange(findActivity(activities, "Total Farmers")),
      total_orders: getMetricChange(findActivity(activities, "Total Orders")),
      total_posts: getMetricChange(findActivity(activities, "Total Posts")),
    }),
    [activityMetrics, activities]
  );

  const companyRows = companyWiseRows;

  const companyTotals = useMemo(
    () =>
      companyRows.reduce(
        (acc, row) => ({
          total_revenue: acc.total_revenue + Number(row.total_revenue || 0),
          full_payment: acc.full_payment + Number(row.full_payment || 0),
          advance_payment: acc.advance_payment + Number(row.advance_payment || 0),
          pending_amount: acc.pending_amount + Number(row.pending_amount || 0),
          credit_orders: acc.credit_orders + Number(row.credit_orders || 0),
        }),
        { total_revenue: 0, full_payment: 0, advance_payment: 0, pending_amount: 0, credit_orders: 0 }
      ),
    [companyRows]
  );

  const dateRangeLabel = useMemo(() => {
    if (!appliedFilters.fromDate && !appliedFilters.toDate) return "All Dates";
    return `${appliedFilters.fromDate || "—"} → ${appliedFilters.toDate || "—"}`;
  }, [appliedFilters.fromDate, appliedFilters.toDate]);

  const salesChart = useMemo(() => {
    const points = Array.isArray(salesOverview?.sales_analytics)
      ? salesOverview.sales_analytics
      : [];
    const categories = points.map((p) => p.label || "");
    const revenue = points.map((p) => Number(p.revenue || 0));
    const orders = points.map((p) => Number(p.orders || 0));
    return {
      options: {
        chart: {
          toolbar: { show: false },
          background: "transparent",
          fontFamily: "inherit",
          zoom: { enabled: false },
        },
        stroke: { curve: "smooth", width: 3 },
        colors: ["#22C55E", "#3B82F6"],
        dataLabels: { enabled: false },
        markers: {
          size: 5,
          strokeWidth: 2,
          strokeColors: "#fff",
          hover: { size: 6 },
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            opacityFrom: 0.38,
            opacityTo: 0.04,
            stops: [0, 100],
          },
        },
        grid: {
          borderColor: "#E2E8F0",
          strokeDashArray: 0,
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: true } },
          padding: { left: 8, right: 8 },
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "12px",
          fontWeight: 500,
          labels: { colors: "#64748B" },
          markers: { width: 8, height: 8, radius: 8 },
          itemMargin: { horizontal: 12 },
        },
        xaxis: {
          categories,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: "#94A3B8", fontSize: "11px" } },
          tooltip: { enabled: false },
        },
        yaxis: [
          {
            min: 0,
            labels: {
              style: { colors: "#94A3B8", fontSize: "11px" },
              formatter: (val) => `₹ ${formatNumber(Math.round(Number(val) || 0))}`,
            },
          },
          {
            opposite: true,
            min: 0,
            labels: {
              style: { colors: "#94A3B8", fontSize: "11px" },
              formatter: (val) => `${Math.round(Number(val) || 0)}`,
            },
          },
        ],
        tooltip: { shared: true },
      },
      series: [
        { name: "Revenue (₹)", data: revenue },
        { name: "Orders", data: orders },
      ],
    };
  }, [salesOverview]);

  const ordersByStatus = useMemo(() => {
    const statuses = Array.isArray(ordersByStatusList) ? ordersByStatusList : [];
    const series = statuses.map((s) => Number(s.count || 0));
    const labels = statuses.map((s) => s.status || "—");
    const total = series.reduce((a, b) => a + b, 0);
    const statusColorMap = {
      pending: "#F59E0B",
      placed: "#F59E0B",
      confirmed: "#3B82F6",
      processing: "#A855F7",
      "ready for dispatch": "#C084FC",
      dispatch: "#16A34A",
      dispatched: "#16A34A",
      delivered: "#14B8A6",
      cancelled: "#EF4444",
      canceled: "#EF4444",
      returned: "#64748B",
    };
    const colors = labels.map(
      (label, i) =>
        statusColorMap[String(label).trim().toLowerCase()] || STATUS_COLORS[i % STATUS_COLORS.length]
    );
    return {
      labels,
      series,
      total,
      legend: labels.map((label, i) => ({
        label,
        count: series[i] || 0,
        percent: total > 0 ? ((series[i] || 0) / total) * 100 : 0,
        color: colors[i],
      })),
      options: {
        chart: {
          background: "transparent",
          toolbar: { show: false },
          parentHeightOffset: 0,
          redrawOnParentResize: true,
          redrawOnWindowResize: true,
        },
        labels,
        colors,
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: { width: 2, colors: [theme.palette.background.paper] },
        plotOptions: {
          pie: {
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  offsetY: -4,
                },
                value: {
                  show: true,
                  fontSize: "22px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  offsetY: 8,
                  formatter: (val) => formatNumber(val),
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Total Orders",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  formatter: () => formatNumber(total),
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 600,
            options: {
              plotOptions: {
                pie: {
                  donut: {
                    labels: {
                      name: { fontSize: "11px" },
                      value: { fontSize: "18px" },
                      total: { fontSize: "11px" },
                    },
                  },
                },
              },
            },
          },
        ],
        tooltip: {
          y: {
            formatter: (val) => formatNumber(val),
          },
        },
      },
    };
  }, [ordersByStatusList, theme]);

  const topCompanies = useMemo(() => {
    const total = companyTotals.total_revenue || 1;
    return [...companyRows]
      .sort((a, b) => b.total_revenue - a.total_revenue)
      .slice(0, 5)
      .map((row, index) => ({
        rank: index + 1,
        title: row.company,
        value: formatCurrency(row.total_revenue),
        percent: `${((row.total_revenue / total) * 100).toFixed(1)}%`,
        color: STATUS_COLORS[index % STATUS_COLORS.length],
      }));
  }, [companyRows, companyTotals]);

  const topProductsList = useMemo(() => {
    const sorted = [...(Array.isArray(topProducts) ? topProducts : [])]
      .sort((a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0))
      .slice(0, 5);
    const revenues = sorted.map((p) => Number(p.total_revenue || 0));
    const total = revenues.reduce((a, b) => a + b, 0) || 1;
    return sorted.map((p, index) => ({
      rank: index + 1,
      title: p.product__name || "Product",
      value: formatCurrency(p.total_revenue),
      percent: `${((Number(p.total_revenue || 0) / total) * 100).toFixed(1)}%`,
      color: STATUS_COLORS[index % STATUS_COLORS.length],
    }));
  }, [topProducts]);

  const farmersSummary = useMemo(() => {
    const rows = Array.isArray(farmersAnalyticsData) ? farmersAnalyticsData : [];
    const sum = (key) => rows.reduce((acc, row) => acc + Number(row?.[key] || 0), 0);
    const total = sum("total_farmers") || metrics.total_farmers || 0;
    const active = sum("active_farmers");
    const neu = sum("new_registrations");
    const repeat = sum("repeat_ordering_farmers");
    const safeTotal = total || 1;
    return {
      total,
      items: [
        { label: "New Farmers", value: neu, percent: (neu / safeTotal) * 100, color: "#22C55E" },
        { label: "Active Farmers", value: active, percent: (active / safeTotal) * 100, color: "#3B82F6" },
        { label: "Repeat Farmers", value: repeat, percent: (repeat / safeTotal) * 100, color: "#A855F7" },
      ],
    };
  }, [farmersAnalyticsData, metrics.total_farmers]);

  const farmersByState = useMemo(() => {
    const rows = Array.isArray(farmersStateWise) ? farmersStateWise : [];
    return rows
      .map((row) => ({
        label: row?.state || row?.state_name || row?.label || "",
        value: Number(row?.value ?? row?.total_farmers ?? row?.count ?? 0),
      }))
      .filter((row) => row.label);
  }, [farmersStateWise]);

  const farmersByDistrict = useMemo(() => {
    const rows = Array.isArray(farmersDistrictWise) ? farmersDistrictWise : [];
    const totals = Object.fromEntries(DISTRICT_FIELDS.map((label) => [label, 0]));
    rows.forEach((row) => {
      const label = canonicalDistrict(row?.district || row?.district_name || row?.label);
      const value = Number(row?.value ?? row?.total_farmers ?? row?.count ?? 0);
      if (!label) {
        totals["Other Districts"] += Number.isNaN(value) ? 0 : value;
        return;
      }
      totals[label] += Number.isNaN(value) ? 0 : value;
    });
    return DISTRICT_FIELDS.map((label) => ({
      label,
      value: totals[label] || 0,
    }));
  }, [farmersDistrictWise]);

  const farmersByCrop = useMemo(() => {
    const rows = Array.isArray(companyWiseCropSales) ? companyWiseCropSales : [];
    const totals = Object.fromEntries(CROP_FIELDS.map((label) => [label, 0]));

    const companyRows =
      cropCompanyFilter === "all"
        ? rows
        : rows.filter(
            (row) =>
              String(row?.company || row?.company_name || "").trim() === cropCompanyFilter
          );

    companyRows.forEach((companyRow) => {
      const crops = Array.isArray(companyRow?.crops) ? companyRow.crops : [];
      crops.forEach((cropRow) => {
        const label = String(cropRow?.crop || cropRow?.crop_name || cropRow?.label || "").trim();
        if (!Object.prototype.hasOwnProperty.call(totals, label)) return;
        const value = Number(
          cropRow?.sales_count ?? cropRow?.value ?? cropRow?.count ?? 0
        );
        totals[label] += Number.isNaN(value) ? 0 : value;
      });
    });

    return CROP_FIELDS.map((label) => ({
      label,
      value: totals[label] || 0,
    }));
  }, [companyWiseCropSales, cropCompanyFilter]);

  const enquirySummary = useMemo(
    () => ({
      total: metrics.total_enquiries,
      // open: findActivityValue(activities, "Open Enquiries") || findActivityValue(activities, "Pending Enquiries"),
      // converted: findActivityValue(activities, "Converted Enquiries"),
    }),
    [metrics.total_enquiries, activities]
  );

  const fieldVisitSummary = useMemo(
    () => ({
      total: metrics.total_field_visits,
      completed: findActivityValue(activities, "Completed Field Visits") || findActivityValue(activities, "Completed Visits"),
      pending: findActivityValue(activities, "Pending Field Visits") || findActivityValue(activities, "Pending Visits"),
    }),
    [metrics.total_field_visits, activities]
  );

  const agronomistSummary = useMemo(
    () => ({
      total: Number(activityMetrics?.talk_to_agronomist?.total || 0),
      connected: Number(activityMetrics?.talk_to_agronomist?.connected || 0),
      pending: Number(activityMetrics?.talk_to_agronomist?.pending || 0),
    }),
    [activityMetrics]
  );

  const attentionItems = [
    // {
    //   label: "Outstanding Amount",
    //   value: formatCurrency(metrics.outstanding_amount),
    //   icon: WarningAmber,
    //   color: "#DC2626",
    //   bgcolor: "#FEF2F2",
    //   borderColor: "#FECACA",
    // },
    // {
    //   label: "Pending Orders",
    //   value: formatNumber(
    //     ordersByStatus.legend.find((s) => /pending|placed/i.test(s.label))?.count || 0
    //   ),
    //   icon: PendingActions,
    //   color: "#EA580C",
    // },
    {
      label: "Pending Enquiries",
      value: formatNumber(activityMetrics?.pending_enquiries),
      icon: Search,
      color: "#EA580C",
      bgcolor: "#FFF7ED",
      borderColor: "#FDBA74",
      path: "/dashboard/enquiry-form",
      subtitle: "Follow up with customers",
    },
    {
      label: "Pending Field Visits",
      value: formatNumber(findActivityValue(activities, "Pending Field Visits")),
      icon: PendingActions,
      color: "#1D4ED8",
      bgcolor: "#EFF6FF",
      borderColor: "#BFDBFE",
      path: "/dashboard/field-visit",
      subtitle: "Complete pending visits",
      iconBg: "#3B82F6",
      iconFg: "#EFF6FF",
      subtitleColor: "#60A5FA",
    },
    {
      label: "Delayed Orders",
      value: formatNumber(findActivityValue(activities, "Delayed Orders")),
      icon: AccessTime,
      color: "#EA580C",
      bgcolor: "#FFF7ED",
      borderColor: "#FDBA74",
      path: "/dashboard/orders-management",
      subtitle: "Check delivery status",
    },
    {
      label: "Pending Talk To Agronomist Request",
      value: formatNumber(
        findActivityValue(activities, "Pending Talk To Agronomist Request") ||
          findActivityValue(activities, "Pending Talk To Agronomist") ||
          findActivityValue(activities, "Pending Agronomist Requests") ||
          findActivityValue(activities, "Talk To Agronomist Pending")
      ),
      icon: SupportAgent,
      color: "#0F766E",
      bgcolor: "#F0FDFA",
      borderColor: "#99F6E4",
      path: "/dashboard/agronomist",
      subtitle: "Respond to pending requests",
      iconBg: "#14B8A6",
      iconFg: "#F0FDFA",
      subtitleColor: "#2DD4BF",
    },
  ];

  if (loading && !metrics.total_revenue && !metrics.total_farmers) {
    return (
      <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box pt={1.5} pb={3} px={{ xs: 0.5, sm: 1 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        mb={2.5}
      >
        <Box>
          {/* <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: "text.primary", letterSpacing: "-0.02em" }}
          >
            CMD Dashboard
          </Typography> */}
          {/* <Stack direction="row" alignItems="center" spacing={0.75} mt={0.5}>
            <Typography variant="body2" color="text.secondary">
              Business Overview | Last Updated: {formatDateTime(lastUpdated)}
            </Typography>
            <IconButton size="small" onClick={handleRefresh} sx={{ color: "primary.main" }}>
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack> */}
        </Box>

        {/* <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Chip
            icon={<CalendarMonth sx={{ fontSize: "18px !important" }} />}
            label={dateRangeLabel}
            deleteIcon={<KeyboardArrowDown />}
            onDelete={() => setShowFilters(true)}
            sx={{
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              fontWeight: 600,
              height: 40,
              "& .MuiChip-label": { px: 1 },
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.15) }}>
              <PersonOutline sx={{ color: "primary.main", fontSize: 20 }} />
            </Avatar>
            <Typography variant="body2" fontWeight={600}>
              {username}
            </Typography>
            <ArrowDropDown sx={{ color: "text.secondary" }} />
          </Stack>
        </Stack> */}
      </Stack>

      {error ? (
        <Card sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        </Card>
      ) : null}

      {/* Filters — same APIs / filters as Old Dashboard */}
      <FilterContainer>
        <FilterHeader>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterList color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                Filters & Search
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                transform: showFilters ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <ExpandMore />
            </IconButton>
          </Stack>
        </FilterHeader>
        <Collapse in={showFilters}>
          <FilterContent>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={2.4}>
                <DatePickerField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setUseDateFilter(true);
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <DatePickerField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setUseDateFilter(true);
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="cmd-companies-label">Company</InputLabel>
                  <Select
                    labelId="cmd-companies-label"
                    multiple
                    value={companiesMenuOpen ? pendingCompanyIds : selectedCompany}
                    label="Company"
                    open={companiesMenuOpen}
                    onOpen={() => {
                      setPendingCompanyIds(
                        Array.isArray(selectedCompany) ? selectedCompany : []
                      );
                      setCompaniesMenuOpen(true);
                    }}
                    onClose={() => setCompaniesMenuOpen(false)}
                    onChange={handleCompanyChange}
                    disabled={isCompanyUser}
                    sx={{
                      borderRadius: 2.5,
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        overflow: "visible",
                        py: 0.75,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 320 },
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected?.length) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            All Companies
                          </Typography>
                        );
                      }
                      const visibleIds = selected.slice(0, 2);
                      const hiddenCount = selected.length - visibleIds.length;
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "nowrap",
                            gap: 0.5,
                            alignItems: "center",
                            width: "100%",
                            minWidth: 0,
                          }}
                        >
                          {visibleIds.map((id) => {
                            const company = companies.find((c) => String(c.id) === String(id));
                            return (
                              <Chip
                                key={id}
                                label={company?.name || id}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{
                                  height: 24,
                                  fontSize: "0.8125rem",
                                  maxWidth: hiddenCount > 0 ? 100 : 150,
                                  flexShrink: 1,
                                  minWidth: 0,
                                  "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              />
                            );
                          })}
                          {hiddenCount > 0 && (
                            <Chip
                              size="small"
                              label={`+${hiddenCount} more`}
                              variant="filled"
                              sx={{
                                height: 24,
                                fontSize: "0.8125rem",
                                flexShrink: 0,
                                fontWeight: 600,
                                maxWidth: "none",
                                bgcolor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                "& .MuiChip-label": {
                                  color: theme.palette.primary.contrastText,
                                  px: 1,
                                },
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                  >
                    {companies.map((company) => {
                      const companyIdStr = String(company.id);
                      const checked =
                        Array.isArray(pendingCompanyIds) &&
                        pendingCompanyIds.map(String).includes(companyIdStr);
                      return (
                        <MenuItem key={company.id} value={companyIdStr}>
                          <Checkbox checked={checked} size="small" sx={{ py: 0 }} />
                          <ListItemText primary={company.name} />
                        </MenuItem>
                      );
                    })}
                    {!isCompanyUser && companies.length > 0 && (
                      <ListSubheader
                        component="div"
                        disableSticky
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          zIndex: 1,
                          backgroundColor: "background.paper",
                          borderTop: "1px solid",
                          borderColor: "divider",
                          py: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              applyCompanySelection(pendingCompanyIds.map(String));
                              setCompaniesMenuOpen(false);
                            }}
                          >
                            Add
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingCompanyIds([]);
                              applyCompanySelection([]);
                              setCompaniesMenuOpen(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </ListSubheader>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="cmd-category-label">Category</InputLabel>
                  <Select
                    labelId="cmd-category-label"
                    value={selectedCategory}
                    label="Category"
                    onChange={handleCategoryChange}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id || cat.name} value={String(cat.id ?? cat.name)}>
                        {cat.name || cat.category_name || cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="cmd-order-type-label">Order Type</InputLabel>
                  <Select
                    labelId="cmd-order-type-label"
                    value={orderType}
                    label="Order Type"
                    onChange={handleOrderTypeChange}
                  >
                    <MenuItem value="all">All Orders</MenuItem>
                    <MenuItem value="normal">App Orders</MenuItem>
                    <MenuItem value="bulk">Bulk Orders</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                  <Button variant="outlined" onClick={handleResetFilters} sx={{ textTransform: "none", fontWeight: 600 }}>
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<FilterList />}
                    onClick={handleApplyFilters}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Apply Filters
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </FilterContent>
        </Collapse>
      </FilterContainer>

      {/* KPI row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
            xl: "repeat(8, minmax(0, 1fr))",
          },
          gap: { xs: 1.25, sm: 1.5 },
          mb: 2.5,
          width: "100%",
        }}
      >
        {KPI_META.map((item) => (
          <KpiCard
            key={item.key}
            label={item.label}
            value={
              item.currency
                ? formatCurrency(metrics[item.key])
                : formatNumber(metrics[item.key])
            }
            icon={item.icon}
            color={item.color}
            negativeTrend={item.negativeTrend}
            change={metricChanges[item.key]}
            fullLabel={item.fullLabel}
          />
        ))}
      </Box>

      {/* Company table + Sales analytics */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} md={12} lg={6}>
          <SectionCard
            sx={{
              p: { xs: 1.5, sm: 2 },
              height: "100%",
              minWidth: 0,
              overflow: "hidden",
              borderRadius: 4,
              border: "1px solid #E2E8F0",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              mb={1.5}
              gap={1}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.25,
                    bgcolor: alpha("#1E3A5F", 0.08),
                    color: "#1E3A5F",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Apartment sx={{ fontSize: 18 }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    wordBreak: "break-word",
                    color: "#1E3A5F",
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    letterSpacing: "-0.01em",
                  }}
                >
                  Company Wise Revenue & Order Details
                </Typography>
              </Stack>
              {/* <Select
                size="small"
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                IconComponent={ArrowDropDown}
                sx={{
                  borderRadius: "10px",
                  minWidth: { xs: "100%", sm: 128 },
                  height: 36,
                  bgcolor: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#1E293B",
                  boxShadow: "none",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "10px",
                    borderColor: "#E2E8F0",
                    borderWidth: "1px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                    borderWidth: "1px",
                  },
                  "& .MuiSelect-select": {
                    py: "8px",
                    px: "14px",
                    pr: "36px !important",
                  },
                  "& .MuiSelect-icon": {
                    color: "#64748B",
                    right: 8,
                    fontSize: 20,
                  },
                }}
              >
                <MenuItem value="this_month">This Month</MenuItem>
              </Select> */}
            </Stack>
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                overflow: "auto",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
              }}
            >
              <Table
                size="small"
                sx={{
                  width: "100%",
                  minWidth: { xs: 640, sm: 720, md: "100%" },
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                  "& .MuiTableCell-root": {
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 1.1, sm: 1.35 },
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                    border: "1px solid #E2E8F0",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  },
                  "& tbody .MuiTableCell-root:first-of-type": {
                    textAlign: "left !important",
                    paddingLeft: "16px !important",
                  },
                  "& tbody .MuiTableRow-root:last-of-type .MuiTableCell-root": {
                    textAlign: "center !important",
                    paddingLeft: "8px !important",
                    paddingRight: "8px !important",
                  },
                  "& thead .MuiTableCell-root, & thead .MuiTableCell-root:first-of-type": {
                    textAlign: "center !important",
                    paddingLeft: "8px !important",
                    paddingRight: "8px !important",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Company", width: "22%", align: "center" },
                      { label: "Total Revenue", width: "15.6%", align: "center" },
                      { label: "Full Payment", width: "15.6%", align: "center" },
                      { label: "Advance Payment", width: "15.6%", align: "center" },
                      { label: "Pending Amount", width: "15.6%", align: "center" },
                      { label: "Credit Orders", width: "15.6%", align: "center" },
                    ].map((h) => (
                      <TableCell
                        key={h.label}
                        align="center"
                        sx={{
                          fontWeight: 700,
                          width: h.width,
                          bgcolor: "#F8FAFC",
                          color: "#1E3A5F",
                          lineHeight: 1.25,
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          textAlign: "center",
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companyRows.map((row) => (
                    <TableRow
                      key={row.company}
                      hover
                      sx={{ "&:hover td": { bgcolor: alpha("#0D9488", 0.02) } }}
                    >
                      <TableCell align="left" sx={{ fontWeight: 500, color: "#334155", textAlign: "left" }}>
                        {row.company}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#334155", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>
                        {formatCurrency(row.total_revenue)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#334155", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>
                        {formatCurrency(row.full_payment)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#334155", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>
                        {formatCurrency(row.advance_payment)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#334155", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>
                        {formatCurrency(row.pending_amount)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#334155", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>
                        {formatCurrency(row.credit_orders)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: "#0D9488",
                        bgcolor: "#ECFDF5",
                        border: "none",
                        textAlign: "center",
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: "#0D9488",
                        bgcolor: "#ECFDF5",
                        border: "none",
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "center",
                      }}
                    >
                      {formatCurrency(companyTotals.total_revenue)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: "#0D9488",
                        bgcolor: "#ECFDF5",
                        border: "none",
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "center",
                      }}
                    >
                      {formatCurrency(companyTotals.full_payment)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: "#0D9488",
                        bgcolor: "#ECFDF5",
                        border: "none",
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "center",
                      }}
                    >
                      {formatCurrency(companyTotals.advance_payment)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: "#0D9488",
                        bgcolor: "#ECFDF5",
                        border: "none",
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "center",
                      }}
                    >
                      {formatCurrency(companyTotals.pending_amount)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: "#0D9488",
                        bgcolor: "#ECFDF5",
                        border: "none",
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "center",
                      }}
                    >
                      {formatCurrency(companyTotals.credit_orders)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={12} lg={6}>
          <SectionCard
            sx={{
              p: { xs: 1.5, sm: 2 },
              height: "100%",
              minWidth: 0,
              borderRadius: 4,
              border: "1px solid #E2E8F0",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <SvgIcon
                  viewBox="0 0 24 24"
                  sx={{ fontSize: 22, color: "#16A34A", flexShrink: 0 }}
                >
                  <path d="M3.5 19V9.6L8.2 13.7 12.6 7.2 16.3 10.4 20.5 4.8V19H3.5Z" />
                </SvgIcon>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: "#1E3A5F", letterSpacing: "-0.01em" }}
                >
                  Sales Analytics
                </Typography>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  border: "1px solid #E2E8F0",
                  borderRadius: "10px",
                  p: "3px",
                  bgcolor: "#fff",
                  flexWrap: "wrap",
                }}
              >
                {salesAnalyticsTypeOptions.map((opt) => {
                  const selected = salesAnalyticsType === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      disableElevation
                      onClick={() => {
                        setSalesAnalyticsType(opt.value);
                        fetchSalesOverview(appliedFilters, opt.value);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        minWidth: { xs: 64, sm: 72 },
                        height: 32,
                        px: 1.5,
                        borderRadius: "8px",
                        boxShadow: "none",
                        border: "none",
                        ...(selected
                          ? {
                              bgcolor: "#145238",
                              color: "#fff",
                              "&:hover": { bgcolor: "#0F3F2B" },
                            }
                          : {
                              bgcolor: "transparent",
                              color: "#64748B",
                              "&:hover": { bgcolor: "#F8FAFC" },
                            }),
                      }}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </Stack>
            </Stack>
            <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
              <Chart type="area" height={280} options={salesChart.options} series={salesChart.series} />
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Orders by status / Top companies / Top products */}
      <Grid
        container
        spacing={{ xs: 1.25, sm: 2 }}
        mb={2.5}
        alignItems="stretch"
        sx={{ width: "100%", maxWidth: "100%" }}
      >
        <Grid item xs={12} lg={4} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <SectionCard
            sx={{
              p: { xs: 1.25, sm: 2 },
              height: "100%",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              mb={1.5}
              sx={{ flexShrink: 0, minWidth: 0 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: "1 1 auto" }}>
                <DonutLarge sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.primary", flexShrink: 0 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  noWrap
                  sx={{ letterSpacing: "-0.01em", fontSize: { xs: "0.95rem", sm: "1rem" }, minWidth: 0 }}
                >
                  Orders by Status
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row", lg: "column", xl: "row" }}
              alignItems="center"
              justifyContent="center"
              spacing={{ xs: 1.5, sm: 2 }}
              sx={{
                flex: 1,
                width: "100%",
                minWidth: 0,
                minHeight: 0,
                px: 0,
              }}
            >
              <Box
                sx={{
                  width: { xs: 156, sm: 180, md: 200 },
                  height: { xs: 156, sm: 180, md: 200 },
                  maxWidth: "100%",
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "& .apexcharts-canvas": {
                    margin: "0 auto !important",
                    maxWidth: "100% !important",
                  },
                }}
              >
                <Chart
                  type="donut"
                  height="100%"
                  width="100%"
                  options={ordersByStatus.options}
                  series={ordersByStatus.series.length ? ordersByStatus.series : [0]}
                />
              </Box>

              <Stack
                spacing={{ xs: 0.75, sm: 1 }}
                justifyContent="center"
                sx={{
                  flex: "1 1 auto",
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                {ordersByStatus.legend.map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    sx={{ minWidth: 0, width: "100%" }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: item.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        title={item.label}
                        sx={{
                          color: "text.primary",
                          fontSize: { xs: "0.72rem", sm: "0.8rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ flexShrink: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ fontVariantNumeric: "tabular-nums", fontSize: "0.8rem" }}
                      >
                        {formatNumber(item.count)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        ({item.percent.toFixed(1)}%)
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
                {!ordersByStatus.legend.length ? (
                  <Typography variant="caption" color="text.secondary">
                    No order status data
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={4} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <SectionCard sx={{ p: { xs: 1.25, sm: 2 }, height: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1.25}
              gap={1}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Apartment sx={{ fontSize: { xs: 18, sm: 20 }, color: "#1E3A5F", flexShrink: 0 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1E3A5F", fontSize: { xs: "0.95rem", sm: "1rem" }, minWidth: 0 }} noWrap>
                  Top Companies
                </Typography>
              </Stack>
            </Stack>

            <Box
              sx={{
                border: "1px solid #E8EEF4",
                borderRadius: "12px",
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": {
                    borderBottom: "1px solid #F1F5F9",
                    borderRight: "none",
                    py: { xs: 1, sm: 1.35 },
                    px: { xs: "4px !important", sm: "10px !important" },
                    verticalAlign: "middle",
                    boxSizing: "border-box",
                  },
                  "& .MuiTableCell-root:first-of-type": {
                    paddingLeft: { xs: "8px !important", sm: "14px !important" },
                    width: { xs: 22, sm: 36 },
                  },
                  "& .MuiTableCell-root:nth-of-type(3)": {
                    width: { xs: 64, sm: 96, md: 110 },
                    minWidth: 0,
                  },
                  "& .MuiTableCell-root:last-of-type": {
                    paddingLeft: "4px !important",
                    paddingRight: { xs: "8px !important", sm: "14px !important" },
                    width: { xs: 86, sm: 88, md: 96 },
                    minWidth: { xs: 86, sm: 0 },
                    boxSizing: "border-box",
                    overflow: "visible",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 800,
                        color: "#1E3A5F",
                        bgcolor: "#F8FAFC",
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        letterSpacing: "0.02em",
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 800,
                        color: "#1E3A5F",
                        bgcolor: "#F8FAFC",
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        letterSpacing: "0.02em",
                      }}
                    >
                      Company
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: { xs: 64, sm: 96, md: 110 },
                        fontWeight: 800,
                        color: "#1E3A5F",
                        bgcolor: "#F8FAFC",
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        letterSpacing: "0.02em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Sales Value
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: { xs: 86, sm: 88, md: 96 },
                        fontWeight: 800,
                        color: "#1E3A5F",
                        bgcolor: "#F8FAFC",
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        letterSpacing: "0",
                        whiteSpace: "nowrap",
                        overflow: "visible",
                      }}
                    >
                      % of Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topCompanies.length ? (
                    topCompanies.map((item) => {
                      const percentValue = parseFloat(item.percent) || 0;
                      return (
                        <TableRow
                          key={`${item.title}-${item.rank}`}
                          sx={{ "&:last-child td": { borderBottom: "none" } }}
                        >
                          <TableCell sx={{ fontWeight: 700, color: "#1E3A5F", fontSize: { xs: "0.72rem", sm: "0.8125rem" } }}>
                            {item.rank}
                          </TableCell>
                          <TableCell sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="flex-start" sx={{ minWidth: 0 }}>
                              <Box
                                sx={{
                                  width: { xs: 22, sm: 28 },
                                  height: { xs: 22, sm: 28 },
                                  borderRadius: "8px",
                                  bgcolor: alpha(item.color, 0.14),
                                  color: item.color,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  mt: 0.1,
                                }}
                              >
                                <LocalFlorist sx={{ fontSize: { xs: 13, sm: 16 } }} />
                              </Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  title={item.title}
                                  sx={{
                                    color: "#1E3A5F",
                                    fontSize: { xs: "0.68rem", sm: "0.78rem" },
                                    lineHeight: 1.3,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {item.title}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(percentValue, 100)}
                                  sx={{
                                    mt: 0.55,
                                    height: 5,
                                    borderRadius: 999,
                                    bgcolor: "#E8F8EE",
                                    "& .MuiLinearProgress-bar": {
                                      borderRadius: 999,
                                      bgcolor: "#22C55E",
                                    },
                                  }}
                                />
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: "#1E3A5F",
                              fontSize: { xs: "0.62rem", sm: "0.78rem" },
                              fontVariantNumeric: "tabular-nums",
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                              overflowWrap: "anywhere",
                              lineHeight: 1.25,
                            }}
                          >
                            {item.value}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color: "#1E3A5F",
                              fontSize: { xs: "0.62rem", sm: "0.78rem" },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.percent}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          No company sales data
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={4} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <SectionCard sx={{ p: { xs: 1.25, sm: 2 }, height: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1.25}
              gap={1}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Assignment sx={{ fontSize: { xs: 18, sm: 20 }, color: "#1E3A5F", flexShrink: 0 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1E3A5F", fontSize: { xs: "0.9rem", sm: "1rem" }, minWidth: 0 }} noWrap>
                  Top Products by Sales
                </Typography>
              </Stack>
            </Stack>

            <Box
              sx={{
                border: "1px solid #E8EEF4",
                borderRadius: "12px",
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <Table
                size="small"
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": {
                    borderBottom: "1px solid #F1F5F9",
                    borderRight: "none",
                    py: { xs: 1, sm: 1.35 },
                    px: { xs: "4px !important", sm: "10px !important" },
                    verticalAlign: "middle",
                    boxSizing: "border-box",
                  },
                  "& .MuiTableCell-root:first-of-type": {
                    paddingLeft: { xs: "8px !important", sm: "14px !important" },
                    width: "auto",
                    minWidth: 0,
                  },
                  "& .MuiTableCell-root:nth-of-type(2)": {
                    width: { xs: 68, sm: 96, md: 108 },
                    minWidth: 0,
                  },
                  "& .MuiTableCell-root:last-of-type": {
                    paddingLeft: "4px !important",
                    paddingRight: { xs: "8px !important", sm: "10px !important" },
                    width: { xs: 86, sm: 88, md: 96 },
                    minWidth: { xs: 86, sm: 0 },
                    maxWidth: "none",
                    boxSizing: "border-box",
                    overflow: "visible",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 800,
                        color: "#1E3A5F",
                        bgcolor: "#F8FAFC",
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        letterSpacing: "0.02em",
                      }}
                    >
                      Product
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: { xs: 68, sm: 96, md: 108 },
                        fontWeight: 800,
                        color: "#1E3A5F",
                        bgcolor: "#F8FAFC",
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        letterSpacing: "0.02em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Sales Value
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: { xs: 86, sm: 88, md: 96 },
                        fontWeight: 800,
                        color: "#1E3A5F",
                        bgcolor: "#F8FAFC",
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        letterSpacing: "0",
                        whiteSpace: "nowrap",
                        overflow: "visible",
                      }}
                    >
                      % of Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topProductsList.length ? (
                    topProductsList.map((item) => {
                      const percentValue = parseFloat(item.percent) || 0;
                      return (
                        <TableRow
                          key={`${item.title}-${item.rank}`}
                          sx={{ "&:last-child td": { borderBottom: "none" } }}
                        >
                          <TableCell sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="flex-start" sx={{ minWidth: 0 }}>
                              <Box
                                sx={{
                                  width: { xs: 22, sm: 28 },
                                  height: { xs: 22, sm: 28 },
                                  borderRadius: "8px",
                                  bgcolor: alpha(item.color, 0.14),
                                  color: item.color,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  mt: 0.1,
                                }}
                              >
                                <LocalFlorist sx={{ fontSize: { xs: 13, sm: 16 } }} />
                              </Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  title={item.title}
                                  sx={{
                                    color: "#1E3A5F",
                                    fontSize: { xs: "0.68rem", sm: "0.78rem" },
                                    lineHeight: 1.3,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {item.title}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(percentValue, 100)}
                                  sx={{
                                    mt: 0.55,
                                    height: 5,
                                    borderRadius: 999,
                                    bgcolor: "#E8F8EE",
                                    "& .MuiLinearProgress-bar": {
                                      borderRadius: 999,
                                      bgcolor: "#22C55E",
                                    },
                                  }}
                                />
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: "#1E3A5F",
                              fontSize: { xs: "0.62rem", sm: "0.78rem" },
                              fontVariantNumeric: "tabular-nums",
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                              overflowWrap: "anywhere",
                              lineHeight: 1.25,
                            }}
                          >
                            {item.value}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color: "#1E3A5F",
                              fontSize: { xs: "0.62rem", sm: "0.78rem" },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.percent}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          No product sales data
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Farmers analytics */}
      <SectionCard sx={{ p: { xs: 1.25, sm: 1.5 }, mb: 2.5, minWidth: 0, overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          mb={1.5}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: alpha("#16A34A", 0.12),
                color: "#16A34A",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <People sx={{ fontSize: 16 }} />
            </Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "text.primary", letterSpacing: "-0.01em" }}
              noWrap
            >
              Farmers Analytics
            </Typography>
          </Stack>
        </Stack>

        {farmersAnalyticsLoading ? (
          <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : farmersAnalyticsError ? (
          <Typography variant="body2" color="error.main" sx={{ py: 1.5 }}>
            {farmersAnalyticsError}
          </Typography>
        ) : (
          <Grid container spacing={1.5} alignItems="stretch">
            {/* Total Farmers compact card */}
            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  height: "100%",
                  minWidth: 0,
                  bgcolor: alpha("#E0F2FE", 0.7),
                  border: `1px solid ${alpha("#0EA5E9", 0.14)}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Stack spacing={1} sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        bgcolor: alpha("#16A34A", 0.14),
                        color: "#16A34A",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Groups sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography
                      fontWeight={700}
                      noWrap
                      sx={{ color: "#0F766E", fontSize: "1.15rem", lineHeight: 1.2, minWidth: 0 }}
                    >
                      Total Farmers
                    </Typography>
                  </Stack>
                  <Box sx={{ pl: "48px", minWidth: 0 }}>
                    <Typography
                      fontWeight={800}
                      sx={{
                        color: "#15803D",
                        fontSize: { xs: "1.25rem", sm: "1.35rem" },
                        lineHeight: 1.2,
                        fontVariantNumeric: "tabular-nums",
                        letterSpacing: "-0.02em",
                        display: "block",
                        textAlign: "left",
                      }}
                    >
                      {formatNumber(farmersSummary.total || metrics.total_farmers)}
                    </Typography>
                    {(() => {
                      const change = metricChanges.total_farmers;
                      const hasChange = change != null && !Number.isNaN(Number(change));
                      if (!hasChange) {
                        return null;
                      }
                      const changeNum = Number(change);
                      const isUp = changeNum >= 0;
                      return (
                        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: isUp ? "#16A34A" : "#DC2626", mt: 0.25 }}>
                          {isUp ? <TrendingUp sx={{ fontSize: 12 }} /> : <TrendingDown sx={{ fontSize: 12 }} />}
                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: "0.7rem" }}>
                            {`${Math.abs(changeNum).toFixed(1)}%`}
                          </Typography>
                        </Stack>
                      );
                    })()}
                  </Box>
                </Stack>
              </Box>
            </Grid>

            {/* Segmentation compact card */}
            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  height: "100%",
                  minWidth: 0,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack spacing={1.1} sx={{ height: "100%", justifyContent: "center" }}>
                  {(() => {
                    const maxItemValue = Math.max(
                      ...farmersSummary.items.map((item) => Number(item.value || 0)),
                      1
                    );
                    return farmersSummary.items.map((item) => {
                      const barValue = Math.min(
                        Math.max((Number(item.value || 0) / maxItemValue) * 100, item.value > 0 ? 10 : 0),
                        100
                      );
                      return (
                        <Box key={item.label} sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{ mb: 0.35, display: "block", color: "text.primary", fontSize: "0.75rem" }}
                          >
                            {item.label}
                          </Typography>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <LinearProgress
                              variant="determinate"
                              value={barValue}
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 999,
                                bgcolor: alpha(item.color, 0.16),
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 999,
                                  bgcolor: item.color,
                                },
                              }}
                            />
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ minWidth: 28, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                            >
                              {formatNumber(item.value)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 36, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: "0.68rem" }}
                            >
                              {item.percent.toFixed(1)}%
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    });
                  })()}
                </Stack>
              </Box>
            </Grid>

            {/* Farmers by State compact card */}
            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  height: "100%",
                  minWidth: 0,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  display="block"
                  mb={1}
                  sx={{ fontSize: "0.8rem", color: "#1E3A5F" }}
                >
                  Farmers by State
                </Typography>
                <Stack spacing={0.9}>
                  {farmersByState.length ? (
                    (() => {
                      const maxValue = Math.max(...farmersByState.map((row) => Number(row.value || 0)), 1);
                      return farmersByState.map((row) => (
                        <Stack
                          key={row.label}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ minWidth: 0 }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            noWrap
                            title={row.label}
                            sx={{
                              width: 92,
                              flexShrink: 0,
                              fontSize: "0.72rem",
                              color: "#334155",
                            }}
                          >
                            {row.label}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((Number(row.value || 0) / maxValue) * 100, 100)}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              height: 8,
                              borderRadius: 999,
                              bgcolor: "#E8F4FC",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 999,
                                background: "linear-gradient(90deg, #22C55E 0%, #14B8A6 100%)",
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                              width: 40,
                              flexShrink: 0,
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                              fontSize: "0.72rem",
                              color: "#1E3A5F",
                            }}
                          >
                            {formatNumber(row.value)}
                          </Typography>
                        </Stack>
                      ));
                    })()
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                      No state-wise data available
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Grid>

            {/* Farmers by District compact card */}
            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  height: "100%",
                  minWidth: 0,
                  bgcolor: "background.paper",
                  border: "1px solid #D6E3EE",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={1}
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ fontSize: "0.8rem", color: "#1E3A5F", flexShrink: 0 }}
                  >
                    Farmers by Crop
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 0, flex: 1, maxWidth: 160 }}>
                    <Select
                      value={
                        cropCompanyFilter === "all" ||
                        cropCompanyOptions.includes(cropCompanyFilter)
                          ? cropCompanyFilter
                          : "all"
                      }
                      onChange={(e) => handleCropCompanyChange(e.target.value)}
                      displayEmpty
                      sx={{
                        fontSize: "0.72rem",
                        height: 28,
                        "& .MuiSelect-select": {
                          py: 0.5,
                          pr: 3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                        All Companies
                      </MenuItem>
                      {cropCompanyOptions.map((company) => (
                        <MenuItem
                          key={company}
                          value={company}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {company}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Stack spacing={0.85}>
                  {farmersByCrop.map((row) => (
                    <Stack
                      key={row.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={0.75}
                      sx={{ minWidth: 0 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          component="span"
                          sx={{
                            width: 20,
                            flexShrink: 0,
                            fontSize: 16,
                            lineHeight: 1,
                            textAlign: "center",
                          }}
                        >
                          {getCropEmoji(row.label)}
                        </Box>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          noWrap
                          title={row.label}
                          sx={{ minWidth: 0, fontSize: "0.78rem", color: "#1E3A5F" }}
                        >
                          {row.label}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                          flexShrink: 0,
                          color: "#16A34A",
                          fontVariantNumeric: "tabular-nums",
                          fontSize: "0.78rem",
                        }}
                      >
                        {formatNumber(row.value)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        )}
      </SectionCard>

      {/* Business activity + Management attention */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={5}>
          <SectionCard sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Business Activity
            </Typography>
            <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch">
              {[
                {
                  title: "Enquiries",
                  items: [
                    { label: "Total", value: enquirySummary.total },
                    // { label: "Open", value: enquirySummary.open },
                    // { label: "Converted", value: enquirySummary.converted },
                  ],
                },
                {
                  title: "Field Visits",
                  items: [
                    { label: "Total", value: fieldVisitSummary.total },
                    { label: "Completed", value: fieldVisitSummary.completed },
                    { label: "Pending", value: fieldVisitSummary.pending },
                  ],
                },
                {
                  title: "Talk To Agronomist",
                  items: [
                    { label: "Total", value: agronomistSummary.total },
                    { label: "Connected", value: agronomistSummary.connected },
                    { label: "Pending", value: agronomistSummary.pending },
                  ],
                },
              ].map((block) => (
                <Grid item xs={12} sm={6} md={4} lg={12} xl={4} key={block.title}>
                  {block.title === "Enquiries" ? (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "14px",
                        border: "1px solid #DDD6FE",
                        bgcolor: "#F5F3FF",
                        height: "100%",
                        overflow: "visible",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: alpha("#7C3AED", 0.12),
                            color: "#6D28D9",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <PersonOutline sx={{ fontSize: 22 }} />
                        </Box>
                        <Typography
                          fontWeight={700}
                          sx={{
                            color: "#6D28D9",
                            fontSize: { xs: "0.85rem", sm: "0.95rem" },
                            letterSpacing: "-0.01em",
                            lineHeight: 1.25,
                            minWidth: 0,
                          }}
                        >
                          {block.title}
                        </Typography>
                      </Stack>
                      <Box sx={{ textAlign: "center", px: 0.5 }}>
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            color: "#94A3B8",
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            lineHeight: 1.2,
                            display: "block",
                            mb: 0.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Total
                        </Typography>
                        <Typography
                          fontWeight={700}
                          sx={{
                            color: "#5B21B6",
                            fontSize: "1.05rem",
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1.2,
                            display: "block",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatNumber(block.items[0]?.value)}
                        </Typography>
                      </Box>
                    </Box>
                  ) : block.title === "Field Visits" ? (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "14px",
                        border: "1px solid #99F6E4",
                        bgcolor: "#F0FDFA",
                        height: "100%",
                        overflow: "visible",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: alpha("#14B8A6", 0.14),
                            color: "#0F766E",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <PersonOutline sx={{ fontSize: 22 }} />
                        </Box>
                        <Typography
                          fontWeight={700}
                          sx={{
                            color: "#0F766E",
                            fontSize: { xs: "0.85rem", sm: "0.95rem" },
                            letterSpacing: "-0.01em",
                            lineHeight: 1.25,
                            minWidth: 0,
                          }}
                        >
                          {block.title}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                      >
                        {block.items.map((item, index) => (
                          <Box
                            key={item.label}
                            sx={{
                              flex: "1 1 0",
                              textAlign: "center",
                              px: 0.5,
                              borderRight:
                                index < block.items.length - 1 ? "1px solid #CDEFEA" : "none",
                            }}
                          >
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                color: "#94A3B8",
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                lineHeight: 1.2,
                                display: "block",
                                mb: 0.5,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              fontWeight={700}
                              sx={{
                                color: "#047857",
                                fontSize: "1.05rem",
                                fontVariantNumeric: "tabular-nums",
                                lineHeight: 1.2,
                                display: "block",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatNumber(item.value)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "14px",
                        border: "1px solid #BFDBFE",
                        bgcolor: "#EFF6FF",
                        height: "100%",
                        overflow: "visible",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: alpha("#3B82F6", 0.14),
                            color: "#1D4ED8",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <SupportAgent sx={{ fontSize: 22 }} />
                        </Box>
                        <Typography
                          fontWeight={700}
                          sx={{
                            color: "#1D4ED8",
                            fontSize: { xs: "0.85rem", sm: "0.95rem" },
                            letterSpacing: "-0.01em",
                            lineHeight: 1.25,
                            minWidth: 0,
                          }}
                        >
                          {block.title}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                      >
                        {block.items.map((item, index) => (
                          <Box
                            key={item.label}
                            sx={{
                              flex: "1 1 0",
                              textAlign: "center",
                              px: 0.5,
                              borderRight:
                                index < block.items.length - 1 ? "1px solid #BFDBFE" : "none",
                            }}
                          >
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                color: "#94A3B8",
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                lineHeight: 1.2,
                                display: "block",
                                mb: 0.5,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              fontWeight={700}
                              sx={{
                                color: "#1E40AF",
                                fontSize: "1.05rem",
                                fontVariantNumeric: "tabular-nums",
                                lineHeight: 1.2,
                                display: "block",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatNumber(item.value)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={7}>
          <SectionCard sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <WarningAmber sx={{ color: "error.main" }} />
                <Typography variant="subtitle1" fontWeight={700} color="error.main">
                  Management Attention Required
                </Typography>
              </Stack>
              {/* <Typography
                variant="body2"
                color="primary.main"
                fontWeight={600}
                sx={{ cursor: "default" }}
              >
                View All
              </Typography> */}
            </Stack>
            <Grid container spacing={1.25}>
              {attentionItems.map((item) => {
                const Icon = item.icon;
                const isHighlightAttentionCard = Boolean(item.subtitle);
                return (
                  <Grid item xs={6} sm={3} md={3} key={item.label}>
                    <Box
                      onClick={() => item.path && navigate(item.path)}
                      sx={{
                        p: isHighlightAttentionCard ? 1.5 : 1.25,
                        borderRadius: isHighlightAttentionCard ? "14px" : 2,
                        border: "1px solid",
                        borderColor: item.borderColor || alpha(item.color, 0.25),
                        bgcolor: item.bgcolor || alpha(item.color, 0.04),
                        height: "100%",
                        minHeight: 96,
                        minWidth: 0,
                        cursor: item.path ? "pointer" : "default",
                      }}
                    >
                      {isHighlightAttentionCard ? (
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              bgcolor: item.iconBg || "#F59E0B",
                              color: item.iconFg || "#FFF7ED",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Icon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ minWidth: 0, pt: 0.15 }}>
                            <Typography
                              fontWeight={600}
                              sx={{ color: item.color, fontSize: "0.82rem", lineHeight: 1.3 }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              fontWeight={700}
                              sx={{ color: item.color, fontSize: "1.35rem", lineHeight: 1.2, mt: 0.35 }}
                            >
                              {item.value}
                            </Typography>
                            <Typography
                              sx={{
                                color: item.subtitleColor || "#F59E0B",
                                fontSize: "0.72rem",
                                lineHeight: 1.3,
                                mt: 0.35,
                              }}
                            >
                              {item.subtitle}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <>
                      <Stack direction="row" spacing={0.75} alignItems="flex-start" mb={0.75}>
                        <Icon sx={{ fontSize: 16, color: item.color, mt: "1px", flexShrink: 0 }} />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ color: "#64748B", fontSize: "0.7rem", lineHeight: 1.3 }}
                        >
                          {item.label}
                        </Typography>
                      </Stack>
                      <Typography variant="body1" fontWeight={700} sx={{ color: "#0F172A", fontSize: "0.95rem" }}>
                        {item.value}
                      </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewDashboard;
